/**
 * B6's numbers, worked out in the browser before anything is sent.
 *
 * Three facts, each of which used to be missing on the live site (2026-10-01):
 *
 *   * the LENGTH, for "uses about {4} of your {90} daily minutes". The bundle
 *     passed '' for both numbers and the banner read "uses about  of your
 *     daily minutes". Display only: the server probes the bytes it receives
 *     and debits from its own number (contracts/api.md §3.3).
 *   * the BUDGET, from `/api/quota`, for the same sentence.
 *   * the BYTE CAP, from `/api/platforms`. A 101 MiB file went to the server,
 *     Cloudflare's free plan answered with its own HTML 413 after 18 s, the
 *     browser sat on the request until the 120 s client timeout, and the page
 *     then said "We couldn't reach the server ... check your connection". A
 *     file over the cap is now refused here, before a byte is sent.
 *
 * None of the three may hold the banner up for long or stop it showing: each
 * one has a timeout, and a number that does not arrive is left out rather than
 * guessed.
 */

/** Bytes in the "MB" the copy and `format.fileSize` both mean. */
export const MIB = 1024 * 1024;

/** A usable length, or null. `Infinity` is what a stream with no length header reports. */
export function validDuration(value: number): number | null {
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** The cap as the copy states it: whole MB, rounded DOWN so it is never overstated. */
export function capMegabytes(capBytes: number | null | undefined): string | null {
  if (typeof capBytes !== 'number' || !Number.isFinite(capBytes) || capBytes <= 0) return null;
  return String(Math.floor(capBytes / MIB));
}

/** Over the cap? Unknown cap means "let the server decide", never "refuse". */
export function overCap(size: number, capBytes: number | null | undefined): boolean {
  return typeof capBytes === 'number' && capBytes > 0 && size > capBytes;
}

/** Resolve to `fallback` if `promise` has not settled (or has rejected) by `ms`. */
export function within<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

/**
 * The file's length in seconds, read from its metadata by an `<audio>`
 * element, or null: a type this browser cannot play, metadata that has not
 * arrived within `timeoutMs`, or no length at all. Never rejects.
 */
export function audioDuration(file: Blob, timeoutMs = 4000): Promise<number | null> {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    let url: string | null = null;
    let settled = false;
    const finish = (value: number | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Let go of the media resource and the object URL; a dropped 90 MB file
      // must not stay pinned in memory for the life of the tab.
      audio.removeAttribute('src');
      audio.load();
      if (url !== null) URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    audio.preload = 'metadata';
    audio.addEventListener('loadedmetadata', () => finish(validDuration(audio.duration)), {
      once: true,
    });
    audio.addEventListener('error', () => finish(null), { once: true });
    try {
      url = URL.createObjectURL(file);
      audio.src = url;
    } catch {
      finish(null);
    }
  });
}
